import { useMemo } from 'react';

function getCssVariable(variableName: string): string {
  return getComputedStyle(document.documentElement).getPropertyValue(variableName).trim();
}

const CSS_VAR_MAPPINGS = {
  'dashboard-background': '--background',
  'dashboard-tile-background': '--card',
  'dashboard-control-background': '--background',
  'dashboard-control-popover-background': '--popover',
  'dashboard-filter-input-background': '--background',
  'dashboard-tile-text-body-color': '--foreground',
  'dashboard-tile-text-secondary-color': '--muted-foreground',
  'dashboard-tile-title-text-color': '--foreground',
  'dashboard-control-text-color': '--foreground',
  'dashboard-control-placeholder-color': '--muted-foreground',
  'dashboard-control-popover-text-color': '--popover-foreground',
  'dashboard-control-popover-secondary-text-color': '--muted-foreground',
  'dashboard-filter-input-text-color': '--foreground',
  'dashboard-filter-input-placeholder-color': '--muted-foreground',
  'dashboard-tile-border-color': '--border',
  'dashboard-control-border-color': '--border',
  'dashboard-control-popover-border-color': '--border',
  'dashboard-control-popover-divider-color': '--border',
  'dashboard-filter-input-border-color': '--input',
  'dashboard-key-color': '--primary',
  'dashboard-key-text-color': '--primary-foreground',
  'dashboard-control-outline-color': '--ring',
  'dashboard-filter-input-outline-color': '--ring',
  'dashboard-filter-input-accent-color': '--primary',
  'dashboard-filter-input-accent-invert-color': '--primary-foreground',
  'dashboard-control-popover-link-color': '--primary',
  'dashboard-button-transparent-text-color': '--foreground',
  'dashboard-button-transparent-interactive-color': '--accent',
  'dashboard-menu-item-interactive-color': '--accent',
  'dashboard-filter-input-token-color': '--secondary',
  'dashboard-filter-input-token-text-color': '--secondary-foreground',
} as const;

const STATIC_VALUES = {
  'dashboard-page-padding': '0',
  'dashboard-tile-border-width': '1px',
  'dashboard-tile-border-style': 'solid',
  'dashboard-tile-border-radius': '0.5rem',
  'dashboard-button-radius': '0.375rem',
  'dashboard-control-radius': '0.5rem',
  'dashboard-control-popover-radius': '0.5rem',
  'dashboard-filter-input-radius': '0.5rem',
  'dashboard-tile-title-font-family': 'Inter, sans-serif',
  'dashboard-tile-text-body-font-family': 'Inter, sans-serif',
} as const;

export type OmniThemeConfig = {
  prefersDark: boolean;
  theme: Record<string, string>;
};

export function useOmniTheme(): OmniThemeConfig {
  return useMemo(() => {
    const themeFromCssVariables = Object.fromEntries(
      Object.entries(CSS_VAR_MAPPINGS).map(([propertyName, cssVar]) => [
        propertyName,
        getCssVariable(cssVar),
      ]),
    );

    return {
      prefersDark: false,
      theme: {
        ...themeFromCssVariables,
        ...STATIC_VALUES,
      },
    };
  }, []);
}
